// ./src/utils/api/BudgetItemService.ts
//
// Event budget item service — an event's expected expenses. OpResult-native,
// event-scoped: every list is filtered by eventId. Full single-item CRUD, a bulk
// category/due/responsible change or remove (array of ids in the where clause),
// and a `summary` query returning the event's rolled-up totals (item count,
// expected, paid, remaining) computed server-side across ALL items. The expected
// amount is DERIVED server-side (fixed, unit × quantity, or a live headcount).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/budget.ts)
// =============================================================================

export const BUDGET_CALC_TYPE = {
  FIXED: "fixed",
  PRICE_QUANTITY: "price_quantity",
  PER_ATTENDEE: "per_attendee",
  PER_ATTENDING: "per_attending",
  PER_ARRIVED: "per_arrived",
  BY_RSVP_GROUP: "by_rsvp_group",
} as const;

export const BUDGET_CALC_TYPE_VALUES = Object.values(BUDGET_CALC_TYPE);

/**
 * #A4.7 — calc types only the APP sets (never offered in the picker, refused from a host's create/update). A line with
 * one of them comes back `isSystem: true`: renamable and categorisable, never removed, its amount not editable.
 */
export const BUDGET_SYSTEM_CALC_TYPE = {
  /** The event's ONE "Staff pay" line — paid volunteers' expected + worked pay (Volunteers → Staff pay). */
  STAFF_PAY: "staff_pay",
  /** #235 — an artist booked on a door deal: what the deal pays on the door so far (Artists → the booking). */
  ARTIST_DEAL: "artist_deal",
} as const;

/** The fields the app keeps on a system line — a host update carrying any of them is refused. */
export const BUDGET_SYSTEM_LOCKED_FIELDS = ["calcType", "unitAmount", "quantity", "rsvpGroupIds", "status"] as const;

// Calc types that multiply a per-unit price by a live guest headcount.
export const BUDGET_PER_HEAD_CALC_TYPES = [
  BUDGET_CALC_TYPE.PER_ATTENDEE,
  BUDGET_CALC_TYPE.PER_ATTENDING,
  BUDGET_CALC_TYPE.PER_ARRIVED,
  BUDGET_CALC_TYPE.BY_RSVP_GROUP,
] as const;

export const BUDGET_PARTY_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VENDOR: "vendor",
  VENUE: "venue",
} as const;

export const BUDGET_PARTY_KIND_VALUES = Object.values(BUDGET_PARTY_KIND);

// =============================================================================
// GraphQL
// =============================================================================

const BUDGET_ITEM_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  description
  glCode
  dueDate
  responsibleKind
  responsibleId
  responsibleLabel
  calcType
  unitAmount
  quantity
  rsvpGroupIds
  currencyCode
  expectedAmount
  paidAmount
  remainingAmount
  lastPaymentAmount
  lastPaymentAt
  lastPaymentMethod
  isSystem
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const BUDGET_ITEM_LIST_QUERY = gql`
  query EventBudgetItemList($filter: EventBudgetItemFilter, $params: PaginationAndSorting) {
    eventBudgetItemList(filter: $filter, params: $params) {
      code
      data { ${BUDGET_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_ITEM_GET_QUERY = gql`
  query EventBudgetItemGet($id: ID) {
    eventBudgetItemGet(id: $id) {
      code
      data { ${BUDGET_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_ITEM_CREATE_MUTATION = gql`
  mutation EventBudgetItemCreate($params: EventBudgetItemInput) {
    eventBudgetItemCreate(params: $params) {
      code
      data { ${BUDGET_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_ITEM_UPDATE_MUTATION = gql`
  mutation EventBudgetItemUpdate($where: EventBudgetItemWhereInput, $params: EventBudgetItemInput) {
    eventBudgetItemUpdate(where: $where, params: $params) {
      code
      data { ${BUDGET_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_ITEM_REMOVE_MUTATION = gql`
  mutation EventBudgetItemRemove($where: EventBudgetItemWhereInput) {
    eventBudgetItemRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const BUDGET_SUMMARY_QUERY = gql`
  query EventBudgetSummary($filter: EventBudgetItemFilter) {
    eventBudgetSummary(filter: $filter) {
      code
      data { itemCount totalExpected totalPaid totalRemaining currencyCode }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventBudgetItem schema)
// =============================================================================

export interface BudgetItem {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  glCode?: string | null;
  dueDate?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  responsibleLabel?: string | null;
  calcType?: string | null;
  unitAmount?: number | null;
  quantity?: number | null;
  rsvpGroupIds?: string[] | null;
  currencyCode?: string | null;
  /** DERIVED server-side: the expected amount. */
  expectedAmount?: number | null;
  /** DERIVED server-side: total paid toward this item. */
  paidAmount?: number | null;
  /** DERIVED server-side: expectedAmount − paidAmount. */
  remainingAmount?: number | null;
  /** DERIVED server-side: the most recent payment's amount / date / method (null if none). */
  lastPaymentAmount?: number | null;
  lastPaymentAt?: string | null;
  lastPaymentMethod?: string | null;
  /** #A4.7 — a line the APP keeps (the event's Staff pay): no remove, amount not editable. */
  isSystem?: boolean | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface BudgetItemInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  glCode?: string | null;
  dueDate?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  calcType?: string | null;
  unitAmount?: number | null;
  quantity?: number | null;
  rsvpGroupIds?: string[] | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may mass-set category, due date or responsible.
export interface BudgetItemBulkInput {
  categoryId?: string | null;
  dueDate?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
}

export interface BudgetItemFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  calcType?: string[] | null;
  responsibleKind?: string[] | null;
  responsibleId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface BudgetItemWhereInput {
  id?: string | string[];
}

export interface BudgetSummary {
  itemCount?: number | null;
  totalExpected?: number | null;
  totalPaid?: number | null;
  totalRemaining?: number | null;
  currencyCode?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class BudgetItemService {
  static async list(
    filter?: BudgetItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(BUDGET_ITEM_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(BUDGET_ITEM_GET_QUERY, { id });
  }

  static async create(params: BudgetItemInput): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_ITEM_CREATE_MUTATION, { params });
  }

  static async update(where: BudgetItemWhereInput, params: BudgetItemInput): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_ITEM_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(BUDGET_ITEM_REMOVE_MUTATION, { where: { id } });
  }

  /** The event's rolled-up budget totals across every item. */
  static async summary(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(BUDGET_SUMMARY_QUERY, { filter: { eventId: [eventId] } });
  }

  /** Mass-set category / due date / responsible on every selected item in one call. */
  static async bulkUpdate(ids: string[], params: BudgetItemBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(BUDGET_ITEM_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected item in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(BUDGET_ITEM_REMOVE_MUTATION, { where: { id: ids } });
  }
}
